import { tokens } from "lib/colors"
import { generateTrainingDate } from "lib/helpers"
import useQueryParams from "hooks/useQueryParams"
import Button from "lib/components/Button"
import CheckBox from "lib/components/Checkbox"
import Icon from "lib/components/Icons"
import Text from "lib/components/Text"
import { ChangeEvent, Fragment, useContext, useEffect, useMemo, useRef, useState } from "react"
import { LanguageContext } from "context/LanguageContext"
import { GetCategoriesQuery } from "@graphql/program/get_categories"
import fetcher from "lib/fetcher"
import useSWR from "swr"
import { keySelectCategory } from "lib/components/CategorySelector"
import { useRouter } from "next/router"
import WrapperAnimateRoute from "@components/shared-components/WrapperAnimate/WrapperAnimateShow"
import CardDateFilter from "@components/shared-components/Card/CardDateFilter"
import CardPublicPriceFilter from "@components/shared-components/Card/CardPublicPriceFilter"
import Select from "lib/components/Select"
import ModalContainer from "../ModalContainer"

import "react-datepicker/dist/react-datepicker.css"


type QueryParamsUrl =
  | "date_range"
  | "categories"
  | "type"
  | "keyword"
  | "min_price"
  | "max_price"
  | "sort_by"
  | "page"
  | "category"

interface Props {
  isOpen: boolean
  categorySelectType: "single" | "multiple"
  onClose: () => void
  resetFilterDate?: () => void
  resetFilterPrice?: () => void
  pathUrl?: string
}

// header component
function HeaderModal() {
  return (
    <div className="fixed flex h-16 w-full items-center border-b bg-white px-4 z-10">
      <p className="text-base font-semibold">Filter</p>
    </div>
  )
}

// footer component
interface PropsFooterModal {
  onSubmitFilter: () => void
  onResetFilter: () => void
}
function FooterModal({ onSubmitFilter, onResetFilter }: PropsFooterModal) {
  const { useTranslation: t } = useContext(LanguageContext)

  return (
    <div className="fixed flex flex-row-reverse justify-between bottom-0 w-full border-t bg-white p-4">
      <Button variant="contained" color="primary" onClick={onSubmitFilter}>
        {`${t("apply")} Filter`}
      </Button>
      <Button colorText="muted" variant="text" color="default" onClick={onResetFilter}>
        {`${t("reset")} Filter`}
      </Button>
    </div>
  )
}

// sort program by
interface PropsSort {
  onSortBy: (event: string) => void
  sortBy: string | string[]
}

function SortProgramBy({ onSortBy, sortBy }: PropsSort) {
  const { useTranslation: t } = useContext(LanguageContext)

  const handleSubmit = (e) => {
    onSortBy(e.target.value)
  }
  return (
    <div className="">
      <Text weight="bold" customClass="block" size="small">
        {t("sort_price")}
      </Text>
      <div className="flex flex-col space-y-2 pt-3">
        <form className="flex flex-col space-y-3">
          <label htmlFor="lowerPrice" className="flex space-x-2">
            <input
              type="radio"
              id="lowerPrice"
              name="lowerPrice"
              value="discount_price:asc"
              onChange={handleSubmit}
              checked={sortBy?.includes("discount_price:asc")}
            />
            <Text size="small" weight="medium">
              {t("low_to_high")}
            </Text>
          </label>
          <label htmlFor="higherPrice" className="flex space-x-2">
            <input
              type="radio"
              id="higherPrice"
              name="higherPrice"
              value="discount_price:desc"
              onChange={handleSubmit}
              checked={sortBy?.includes("discount_price:desc")}
            />
            <Text size="small" weight="medium">
              {t("high_to_low")}
            </Text>
          </label>
        </form>
      </div>
    </div>
  )
}

function ModalAllFiltersPublic({
  isOpen,
  onClose,
  resetFilterDate,
  resetFilterPrice,
  pathUrl = "/public-class",
}: Props) {
  const router = useRouter()
  const { useTranslation: t, language } = useContext(LanguageContext)
  const { category: categorySlug, date_range, min_price, max_price } = router.query
  const queries = useQueryParams()
  const [selectedMinPrice, setSelectedMinPrice] = useState<string>(null)
  const [selectedMaxPrice, setSelectedMaxPrice] = useState<string>(null)
  const [sortBy, setSortBy] = useState<string | string[]>(null)
  const [isCardDateOpen, setCardDate] = useState(false)
  const [isCardPriceOpen, setCardPrice] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [categoryAllData, setCategoryAllData] = useState([])
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])

  const categoriesRef = useRef<HTMLDivElement>()
  const dateRef = useRef<HTMLDivElement>()
  const priceRef = useRef<HTMLDivElement>()

  const { data: allCategories } = useSWR<GetCategoriesQuery>("/api/categories", fetcher)

  function setQueryParams(key: QueryParamsUrl, value: string) {
    queries.set(key, value)
  }
  function deleteQueryParams(key: QueryParamsUrl) {
    queries.delete(key)
  }

  const programTypeList = useMemo(
    () => [
      {
        label: "In-house Program",
        value: "inhouse",
      },
      {
        label: t("public_class"),
        value: "public_class",
      },
      {
        label: "Skill Booster",
        value: "skill_booster",
      },
      {
        label: t("certification"),
        value: "certification",
      },
    ],
    [t],
  )

  const typesQueryValue = queries?.get("type")
  useEffect(() => {
    if (typesQueryValue) {
      const queryTypes = typesQueryValue?.split(",") || []
      setSelectedTypes(
        programTypeList.filter((it) => queryTypes.includes(it.value)).map((it) => it.value),
      )
    }
  }, [typesQueryValue, programTypeList])

  const dateRangeQueryValue = queries?.get("date_range")
  useEffect(() => {
    if (dateRangeQueryValue) {
      const queryDateRange = dateRangeQueryValue?.split(",") || []
      setSelectedDate(queryDateRange)
    }
  }, [dateRangeQueryValue])

  const minPriceQueryValue = queries?.get("min_price")
  const maxPriceQueryValue = queries?.get("max_price")
  useEffect(() => {
    if (maxPriceQueryValue || minPriceQueryValue) {
      setSelectedMinPrice(maxPriceQueryValue)
      setSelectedMaxPrice(minPriceQueryValue)
    }
  }, [minPriceQueryValue, maxPriceQueryValue])

  useEffect(() => {
    if (allCategories) {
      let keySelectorCategory: keySelectCategory = "category_name"

      if (language === "ID") {
        keySelectorCategory = "category_name_indo"
      }

      const categoryProgram = allCategories.program_category || []
      const allCategorySlugs = categoryProgram
        .filter((category) => category.categories.length === 0)
        .map((category) => category.slug)

      const allDataCategory = [
        { value: "all", label: t("all_categories") },
        ...categoryProgram
          .filter((category) => category.categories.length === 0)
          .map((category) => ({
            label: category[keySelectorCategory] || category.category_name,
            value: category.slug,
            description: category.category_description,
          })),
          ...categoryProgram
          .filter((category) => category.categories.length > 0)
          .map((category) => ({
            label: category[keySelectorCategory] || category.category_name,
            value: category.slug,
            description: category.category_description,
          })),
      ].sort((a, b) => a.label.localeCompare(b.label))

      const selectedValues: string[] = []
      const urlSearchParams = new URLSearchParams(window.location.search)
      const categoriesParam = urlSearchParams.get("categories")

      if (categorySlug === "all") {
        if (categoriesParam) {
          selectedValues.push(...categoriesParam.split(","))
        } else {
          selectedValues.push(...allCategorySlugs)
        }
      } else if (typeof categorySlug === "string") {
        selectedValues.push(categorySlug)
      }

      const allSelected = allCategorySlugs.every((slug) => selectedValues.includes(slug))

      let finalSelectedOptions = []
      if (allSelected) {
        finalSelectedOptions = []
      } else {
        const selectedOptions = allDataCategory.filter((item) =>
          selectedValues.includes(item.value),
        )
        finalSelectedOptions = selectedOptions.length > 0 ? selectedOptions : []
      }

      setSelectedCategory(finalSelectedOptions)
      setCategoryAllData(allDataCategory)
    }

    return () => {
      setCategoryAllData([])
    }
  }, [allCategories, language, t, categorySlug])

  const handleProgramTypes = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedTypes(() => {
      if (e.target.checked) {
        if (selectedTypes?.length > 0) {
          return [...selectedTypes.filter((val) => val !== e.target.value), e.target.value]
        }
        return [e.target.value]
      }
      return [...selectedTypes.filter((val) => val !== e.target.value)]
    })
  }

  // handle sort
  const handleSort = (e) => {
    setSortBy(e)
  }

  const handleFilterDate = (selectedDateValue) => {
    setQueryParams("date_range", selectedDateValue?.toString())
  }

  const handleFilterPrices = (minValuePricing, maxValuePricing) => {
    if (minValuePricing) {
      setQueryParams("min_price", minValuePricing?.toString())
    } else {
      deleteQueryParams("min_price")
    }

    if (maxValuePricing) {
      setQueryParams("max_price", maxValuePricing?.toString())
    } else {
      deleteQueryParams("max_price")
    }
  }

  const handleFilterProgramType = (value) => {
    setQueryParams("type", value?.toString())
  }

  const handleFilterSort = (value) => {
    setQueryParams("sort_by", value?.toString())
  }

  // apply filter
  const applyFilter = () => {
    if (selectedDate) {
      handleFilterDate(selectedDate)
    }
    if (selectedMinPrice || selectedMaxPrice) {
      handleFilterPrices(selectedMinPrice, selectedMaxPrice)
    }
    handleFilterProgramType(selectedTypes)
    if (sortBy) {
      handleFilterSort(sortBy)
    }

    let pathnameValue = ""
    const selectedValues = selectedCategory.map((opt) => opt.value)

    if (selectedValues.length === 1 && selectedValues[0] !== "all") {
      pathnameValue = `${pathUrl}/${selectedValues[0]}`
      deleteQueryParams("categories")
    } else {
      pathnameValue = `${pathUrl}/all`

      if (selectedValues.includes("all") && selectedCategory.length === categoryAllData.length) {
        deleteQueryParams("categories")
      } else {
        setQueryParams("categories", selectedValues.join(","))
      }
    }
    const queryString = queries.toString()
    const finalUrl = queryString.length ? `${pathnameValue}?${queryString}` : pathnameValue

    router.push(finalUrl)

    onClose()
  }

  const resetAllFilter = () => {
    setSelectedCategory([{ value: "all", label: t("all_categories") }])
    setSelectedTypes(null)
    setSelectedDate(null)
    setSelectedMaxPrice(null)
    setSelectedMinPrice(null)
    router.push(`${pathUrl}/all`)
  }

  useEffect(() => {
    if (isOpen) {
      window.scrollTo(0, 1)
    }
  }, [isOpen])

  function showPrice(minPrice, maxPrice) {
    let minPriceValue = ""
    let maxPriceValue = ""
    let charStrip = ""
    let idrCode = ""
    let title = ""
    if (minPrice && maxPrice > 0) {
      idrCode = "Rp"
      minPriceValue = parseInt(minPrice, 10).toLocaleString("id-ID")
      maxPriceValue = parseInt(maxPrice, 10).toLocaleString("id-ID")
      charStrip = "-"
    }

    if (minPrice && !maxPrice) {
      idrCode = "Rp"
      minPriceValue = parseInt(minPrice, 10).toLocaleString("id-ID")

      charStrip = ""
    }
    if (maxPrice > 0 && !minPrice) {
      idrCode = "Rp"
      maxPriceValue = parseInt(maxPrice, 10).toLocaleString("id-ID")
      minPriceValue = "0"

      charStrip = "-"
    }
    if (!minPrice && !maxPrice) {
      title = t("price")
    }

    return `${idrCode} ${minPriceValue} ${charStrip} ${maxPriceValue} ${title}`
  }

  return (
    <ModalContainer
      closeModal={onClose}
      isModalOpen={isOpen}
      positionTopCloseX="top-2.5"
      positionCloseX="right-3">
      <div className="h-screen w-screen overflow-x-hidden overflow-y-scroll border bg-white pb-8">
        {isOpen && (
          <style>{`
            .qcw-trigger-btn {
              display: none !important;
            }
          `}</style>
        )}
        <HeaderModal />
        <div className="mb-16 pb-2" />
        <div className="flex flex-col gap-y-5 p-3">
          <div className="w-full" ref={categoriesRef}>
            <Text weight="bold" customClass="mb-2" size="small">
              {t("categories")}
            </Text>
            <Select
              isMulti
              variant="checkbox"
              label=""
              value={selectedCategory}
              onChange={(selectedOptions: { label: string; value: string }[]) => {
                let updatedSelectedOptions = [...selectedOptions]

                const isSelectingAll = updatedSelectedOptions.some((opt) => opt.value === "all")
                const wasSelectingAll = selectedCategory.some((opt) => opt.value === "all")
                const isFullySelected = selectedCategory.length === categoryAllData.length

                // If selects "All" when it wasn't previously selected
                if (isSelectingAll && !wasSelectingAll) {
                  updatedSelectedOptions = categoryAllData
                }
                // If deselects "All" when it was previously selected
                else if (!isSelectingAll && wasSelectingAll) {
                  updatedSelectedOptions = []
                }
                // If selects multiple categories (not "All"), so ensure "All" is not selected
                else if (!isSelectingAll && !wasSelectingAll && updatedSelectedOptions.length > 1) {
                  updatedSelectedOptions = updatedSelectedOptions.filter(
                    (opt) => opt.value !== "all",
                  )
                }
                // If had all categories selected and clicks "All" again to deselect everything
                else if (isSelectingAll && wasSelectingAll && isFullySelected) {
                  const difference = selectedCategory.filter(
                    (prevOpt) => !selectedOptions.some((opt) => opt.value === prevOpt.value),
                  )
                  updatedSelectedOptions = difference
                }
                // If all options (excluding "All") are unselected, fallback to []
                else if (!wasSelectingAll && updatedSelectedOptions.length === 0) {
                  updatedSelectedOptions = []
                }

                setSelectedCategory(updatedSelectedOptions)
              }}
              placeholder={t("select_category")}
              options={categoryAllData}
              customCss={{ borderRadius: "6px" }}
              noOptionsMessage={t("not_available")}
            />
          </div>
          <div className="flex flex-col">
            <Text weight="bold" customClass="mb-2" size="small">
              {t("range_date")}
            </Text>
            <div
              ref={dateRef}
              className={`relative border w-full ${
                date_range?.length > 0 ? "border-primary" : "border-neutral-40"
              } font-heading group flex-auto items-center rounded bg-white text-sm font-medium`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setCardPrice(false)
                  setCardDate(!isCardDateOpen)
                }}
                className="relative flex h-[37px] w-full items-center justify-between pr-2 pl-4 rounded">
                <Text
                  color={date_range?.length > 0 || selectedDate?.length > 0 ? "gray" : "muted"}
                  weight="medium"
                  size="small"
                  customClass="leading-5">
                  {`${
                    date_range || selectedDate
                      ? generateTrainingDate(
                          new Date(selectedDate?.toString().split(",")?.[0]),
                          new Date(selectedDate?.toString().split(",")?.[1]),
                        )
                      : t("range_date")
                  }`}
                </Text>

                <Icon
                  iconName="arrowBottom"
                  styles={`transition ${isCardDateOpen ? "rotate-180" : "rotate-0"}`}
                  color={tokens.neutral[40]}
                />
              </div>
              <WrapperAnimateRoute
                type="show"
                isLoading={!isCardDateOpen}
                className={`absolute w-full z-10 ${isCardDateOpen ? "top-9" : "hidden"}`}>
                <div ref={dateRef}>
                  <CardDateFilter
                    applyFilterDate={setSelectedDate}
                    resetFilterDate={resetFilterDate}
                    isDesktop={false}
                    setOpenCard={setCardDate}
                  />
                </div>
              </WrapperAnimateRoute>
            </div>
          </div>
          <div className="flex flex-col">
            <Text weight="bold" customClass="mb-2" size="small">
              {t("price")}
            </Text>
            <div
              ref={priceRef}
              className={`relative border w-full ${
                min_price?.length > 0 || max_price?.length > 0
                  ? "border-primary"
                  : "border-neutral-40"
              } font-heading group flex-auto items-center rounded bg-white text-sm font-medium`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setCardDate(false)
                  setCardPrice(!isCardPriceOpen)
                }}
                className="relative flex h-[37px] w-full items-center justify-between pr-2 pl-4">
                <Text
                  color={min_price?.length > 0 || max_price?.length > 0 ? "gray" : "muted"}
                  weight="medium"
                  size="small"
                  customClass="leading-5">
                  {showPrice(selectedMinPrice, selectedMaxPrice)}
                </Text>

                <Icon
                  iconName="arrowBottom"
                  styles={`transition ${isCardPriceOpen ? "rotate-180" : "rotate-0"}`}
                  color={tokens.neutral[40]}
                />
              </div>

              <WrapperAnimateRoute
                type="show"
                isLoading={!isCardPriceOpen}
                className={`absolute z-20 w-screen ${isCardPriceOpen ? "top-11" : "hidden"}`}>
                <div className="pr-10">
                  <CardPublicPriceFilter
                    handleSubmitFilterPrices={(min, max) => {
                      setSelectedMinPrice(min?.toString())
                      setSelectedMaxPrice(max?.toString())
                    }}
                    handleResetPrice={resetFilterPrice}
                    withRangeLine={false}
                    setOpenCard={setCardPrice}
                    isDesktop={false}
                  />
                </div>
              </WrapperAnimateRoute>
            </div>
          </div>
          <div className="flex flex-col">
            <Text weight="bold" customClass="block" size="small">
              {t("program_and_class_type")}
            </Text>
            <div className="flex flex-col w-full gap-2 pt-3">
              {programTypeList.map((it, index) => (
                <Fragment key={index}>
                  <CheckBox
                    label={it.label}
                    onChange={handleProgramTypes}
                    value={it.value}
                    checked={selectedTypes?.includes(it.value)}
                  />
                </Fragment>
              ))}
            </div>
          </div>
          <SortProgramBy onSortBy={handleSort} sortBy={sortBy} />
        </div>
      </div>
      <FooterModal onSubmitFilter={applyFilter} onResetFilter={resetAllFilter} />
    </ModalContainer>
  )
}

export default ModalAllFiltersPublic
